import { type Action, isAnyOf } from '@reduxjs/toolkit'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import { type TruckConfiguration, truckConfigurationSlice } from '#app/store/slices/configurationSlice'
import { UntitledConfigurationPlaceholder } from '#features/Configuration/ConfigurationTitle'
import { createInitialSelection } from '#pages/Configuration/utils/utils'
import type { AppMiddleware } from '../configurationRequestMiddleware'

export type ConfigurationPlaceReason =
  | 'variant-selection'
  | 'step-setting'
  | 'commit-undoing'
  | 'change-group-of-interest'
  | 'unknown'
  | 'cloning'
  | 'refreshed'
  | 'tab-recovery'
  | 'mock'
const getConfigurationPlacingReason = (action: Action): ConfigurationPlaceReason => {
  if (
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidSelectVariantAndVariantId.matchFulfilled(action) ||
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidSelectVariants.matchFulfilled(action)
  ) {
    return 'variant-selection'
  }
  if (enhancedTmsApiService.endpoints.postApiConfigurationByUuidStepAndStep.matchFulfilled(action)) {
    return 'step-setting'
  }
  if (enhancedTmsApiService.endpoints.postApiConfigurationByUuidUndocommit.matchFulfilled(action)) {
    return 'commit-undoing'
  }
  if (enhancedTmsApiService.endpoints.putApiConfigurationByUuidChangeGroupOfInterest.matchFulfilled(action)) {
    return 'change-group-of-interest'
  }
  return 'unknown'
}

const endpointMatchers = (
  [
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidSelectVariantAndVariantId,
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidStepAndStep,
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidUndocommit,
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidSelectVariants,
    enhancedTmsApiService.endpoints.postApiConfigurationByUuidDeselectVariants,
    enhancedTmsApiService.endpoints.putApiConfigurationByUuidChangeGroupOfInterest,
    enhancedTmsApiService.endpoints.getApiConfigurationByUuid,
  ] as const
).map((x) => x.matchFulfilled)

const endpointMatcher = isAnyOf(...endpointMatchers)

export const apiChangeListener = (middleware: AppMiddleware) => {
  middleware.startListening({
    matcher: endpointMatcher,
    effect: (action, listenerApi) => {
      if (!isAnyOf(...endpointMatchers)(action)) {
        return
      }

      const state = listenerApi.getState().truckConfigurations
      const uuid = action.meta.arg.originalArgs.uuid
      const config = Object.values(state.configurations).find((x) => x?.uuid === uuid)
      const copiedConfiguration = config ? structuredClone(config) : undefined
      if (!copiedConfiguration) {
        return
      }

      const configuration: TruckConfiguration = {
        lastChange: undefined,
        ...copiedConfiguration,
        uuid,
        title: !copiedConfiguration.title ? UntitledConfigurationPlaceholder : copiedConfiguration.title,
        marketDenomination: action.payload.marketDenomination ?? copiedConfiguration.marketDenomination,
        step: {
          ...copiedConfiguration.step,
          system: action.payload.currentStep,
          loading: undefined,
          error: undefined,
        },
        lastApiInteraction: action.meta.fulfilledTimeStamp as Timestamp,
        productGroups: action.payload.productGroups,
      }
      configuration.initialSelection = createInitialSelection(configuration)

      listenerApi.dispatch(truckConfigurationSlice.actions._placeConfiguration({ configuration, reason: getConfigurationPlacingReason(action) }))
    },
  })
}
